import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type PaymentType = 'monthly' | '3_pay' | 'annual' | 'other' | '';
export type PaymentMethod =
  | 'credit_card'
  | 'eft'
  | 'direct_bill'
  | 'agency_bill'
  | 'other'
  | '';
export type DownPaymentType = 'e_transfer' | 'cash' | 'cheque' | 'other' | '';
export type FinancingType = 'paid_full' | 'premium_financing' | '';
export type PaperCopyReason =
  | 'vulnerable_customer'
  | 'no_email_internet'
  | 'customer_requests_paper'
  | '';

export interface LegacyAutoClientInformation {
  client_name: string;
  client_code: string;
  client_email: string;
  is_remarket: boolean;
  has_loa: boolean;
}

export interface LegacyAutoBinderInformation {
  binder_number: string;
  effective_date: string;
  insurance_company: string;
  premium: string;
}

export interface LegacyAutoAgencyBilling {
  financing_type: FinancingType;
  is_signed_contract_attached: boolean;
  province_code: string;
  business_code: string;
  total_premium: string;
  company_fee: string;
  broker_fee: string;
  tax: string;
}

export interface LegacyAutoBilling {
  payment_date_requested: string;
  payment_type: PaymentType;
  payment_method: PaymentMethod;
  down_payment: boolean;
  down_payment_amount: number;
  down_payment_type: DownPaymentType;
  agency_billing: LegacyAutoAgencyBilling;
}

export interface LegacyAutoTelematics {
  has_telematics: boolean;
  driver_name: string;
  phone: string;
  email: string;
}

export interface LegacyAutoAdditionalInfo {
  all_documents_attached: boolean;
  underwriting_approval_required: boolean;
  underwriting_reason: string;
  paper_copies_required: boolean;
  paper_copy_reason: PaperCopyReason;
  additional_broker_note: string;
}

export interface LegacyAutoFormData {
  client_information: LegacyAutoClientInformation;
  binder_information: LegacyAutoBinderInformation;
  billing: LegacyAutoBilling;
  telematics: LegacyAutoTelematics;
  additional_info: LegacyAutoAdditionalInfo;
}

export interface LegacyAutoFormState {
  data: LegacyAutoFormData;
  isDataSet: boolean;
  hasUnsavedChanges: boolean;
  validationErrors: Record<string, any>;
  wipStatus: number | null;
  premiumConfirmed: boolean;
}

const initialClientInformation: LegacyAutoClientInformation = {
  client_name: '',
  client_code: '',
  client_email: '',
  is_remarket: false,
  has_loa: false,
};

const initialBinderInformation: LegacyAutoBinderInformation = {
  binder_number: '',
  effective_date: '',
  insurance_company: '',
  premium: '',
};

const initialAgencyBilling: LegacyAutoAgencyBilling = {
  financing_type: '',
  is_signed_contract_attached: false,
  province_code: '',
  business_code: 'AUTO',
  total_premium: '',
  company_fee: '',
  broker_fee: '',
  tax: '',
};

const initialBilling: LegacyAutoBilling = {
  payment_date_requested: '',
  payment_type: '',
  payment_method: '',
  down_payment: false,
  down_payment_amount: 0,
  down_payment_type: '',
  agency_billing: initialAgencyBilling,
};

const initialTelematics: LegacyAutoTelematics = {
  has_telematics: false,
  driver_name: '',
  phone: '',
  email: '',
};

const initialAdditionalInfo: LegacyAutoAdditionalInfo = {
  all_documents_attached: false,
  underwriting_approval_required: false,
  underwriting_reason: '',
  paper_copies_required: false,
  paper_copy_reason: '',
  additional_broker_note: '',
};

const initialState: LegacyAutoFormState = {
  data: {
    client_information: initialClientInformation,
    binder_information: initialBinderInformation,
    billing: initialBilling,
    telematics: initialTelematics,
    additional_info: initialAdditionalInfo,
  },
  isDataSet: false,
  hasUnsavedChanges: false,
  validationErrors: {},
  wipStatus: null,
  premiumConfirmed: false,
};

const legacyAutoFormSlice = createSlice({
  name: 'legacyAutoForm',
  initialState,
  reducers: {
    // Client Information
    setClientName: (state, action: PayloadAction<string>) => {
      state.data.client_information.client_name = action.payload;
      state.hasUnsavedChanges = true;
    },
    setClientCode: (state, action: PayloadAction<string>) => {
      state.data.client_information.client_code = action.payload;
      state.hasUnsavedChanges = true;
    },
    setClientEmail: (state, action: PayloadAction<string>) => {
      state.data.client_information.client_email = action.payload;
      state.hasUnsavedChanges = true;
    },
    setIsRemarket: (state, action: PayloadAction<boolean>) => {
      state.data.client_information.is_remarket = action.payload;
      state.hasUnsavedChanges = true;
    },
    setHasLoa: (state, action: PayloadAction<boolean>) => {
      state.data.client_information.has_loa = action.payload;
      state.hasUnsavedChanges = true;
    },

    // Binder Information
    setBinderNumber: (state, action: PayloadAction<string>) => {
      state.data.binder_information.binder_number = action.payload;
      state.hasUnsavedChanges = true;
    },
    setEffectiveDate: (state, action: PayloadAction<string>) => {
      state.data.binder_information.effective_date = action.payload;
      state.hasUnsavedChanges = true;
    },
    setInsuranceCompany: (state, action: PayloadAction<string>) => {
      state.data.binder_information.insurance_company = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPremium: (state, action: PayloadAction<string>) => {
      state.data.binder_information.premium = action.payload;
      state.hasUnsavedChanges = true;
    },

    // Telematics
    setHasTelematics: (state, action: PayloadAction<boolean>) => {
      state.data.telematics.has_telematics = action.payload;
      if (!action.payload) {
        state.data.telematics.driver_name = '';
        state.data.telematics.phone = '';
        state.data.telematics.email = '';
      }
      state.hasUnsavedChanges = true;
    },
    setTelematicsDriverName: (state, action: PayloadAction<string>) => {
      state.data.telematics.driver_name = action.payload;
      state.hasUnsavedChanges = true;
    },
    setTelematicsPhone: (state, action: PayloadAction<string>) => {
      state.data.telematics.phone = action.payload;
      state.hasUnsavedChanges = true;
    },
    setTelematicsEmail: (state, action: PayloadAction<string>) => {
      state.data.telematics.email = action.payload;
      state.hasUnsavedChanges = true;
    },

    // Additional Info
    setAllDocumentsAttached: (state, action: PayloadAction<boolean>) => {
      state.data.additional_info.all_documents_attached = action.payload;
      state.hasUnsavedChanges = true;
    },
    setUnderwritingApprovalRequired: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.data.additional_info.underwriting_approval_required =
        action.payload;
      if (!action.payload) {
        state.data.additional_info.underwriting_reason = '';
      }
      state.hasUnsavedChanges = true;
    },
    setUnderwritingReason: (state, action: PayloadAction<string>) => {
      state.data.additional_info.underwriting_reason = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPaperCopiesRequired: (state, action: PayloadAction<boolean>) => {
      state.data.additional_info.paper_copies_required = action.payload;
      if (!action.payload) {
        state.data.additional_info.paper_copy_reason = '';
      }
      state.hasUnsavedChanges = true;
    },
    setPaperCopyReason: (state, action: PayloadAction<PaperCopyReason>) => {
      state.data.additional_info.paper_copy_reason = action.payload;
      state.hasUnsavedChanges = true;
    },
    setAdditionalBrokerNote: (state, action: PayloadAction<string>) => {
      state.data.additional_info.additional_broker_note = action.payload;
      state.hasUnsavedChanges = true;
    },

    // Billing
    setPaymentDateRequested: (state, action: PayloadAction<string>) => {
      state.data.billing.payment_date_requested = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPaymentType: (state, action: PayloadAction<PaymentType>) => {
      state.data.billing.payment_type = action.payload;
      state.hasUnsavedChanges = true;
    },
    setPaymentMethod: (state, action: PayloadAction<PaymentMethod>) => {
      state.data.billing.payment_method = action.payload;
      state.hasUnsavedChanges = true;
    },
    setDownPayment: (state, action: PayloadAction<boolean>) => {
      state.data.billing.down_payment = action.payload;
      if (!action.payload) {
        state.data.billing.down_payment_amount = 0;
        state.data.billing.down_payment_type = '';
      }
      state.hasUnsavedChanges = true;
    },
    setDownPaymentAmount: (state, action: PayloadAction<number>) => {
      state.data.billing.down_payment_amount = action.payload;
      state.hasUnsavedChanges = true;
    },
    setDownPaymentType: (state, action: PayloadAction<DownPaymentType>) => {
      state.data.billing.down_payment_type = action.payload;
      state.hasUnsavedChanges = true;
    },

    // Agency Billing
    setAgencyBillingFinancingType: (
      state,
      action: PayloadAction<FinancingType>,
    ) => {
      state.data.billing.agency_billing.financing_type = action.payload;
      if (action.payload !== 'premium_financing') {
        state.data.billing.agency_billing.is_signed_contract_attached = false;
      }
      state.hasUnsavedChanges = true;
    },
    setAgencyBillingSignedContractAttached: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.data.billing.agency_billing.is_signed_contract_attached =
        action.payload;
      state.hasUnsavedChanges = true;
    },
    setAgencyBillingProvinceCode: (state, action: PayloadAction<string>) => {
      state.data.billing.agency_billing.province_code = action.payload;
      state.hasUnsavedChanges = true;
    },
    setAgencyBillingBusinessCode: (state, action: PayloadAction<string>) => {
      state.data.billing.agency_billing.business_code = action.payload;
      state.hasUnsavedChanges = true;
    },
    setAgencyBillingTotalPremium: (state, action: PayloadAction<string>) => {
      state.data.billing.agency_billing.total_premium = action.payload;
      // Reset premium confirmation if premium drops to or below threshold
      const premiumValue = parseFloat(action.payload || '0');
      if (premiumValue <= 10000) {
        state.premiumConfirmed = false;
      }
      state.hasUnsavedChanges = true;
    },
    setPremiumConfirmed: (state, action: PayloadAction<boolean>) => {
      state.premiumConfirmed = action.payload;
    },
    setAgencyBillingCompanyFee: (state, action: PayloadAction<string>) => {
      state.data.billing.agency_billing.company_fee = action.payload;
      state.hasUnsavedChanges = true;
    },
    setAgencyBillingBrokerFee: (state, action: PayloadAction<string>) => {
      state.data.billing.agency_billing.broker_fee = action.payload;
      state.hasUnsavedChanges = true;
    },
    setAgencyBillingTax: (state, action: PayloadAction<string>) => {
      state.data.billing.agency_billing.tax = action.payload;
      state.hasUnsavedChanges = true;
    },

    // Form State
    setDataSet: (state, action: PayloadAction<boolean>) => {
      state.isDataSet = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    setValidationErrors: (
      state,
      action: PayloadAction<Record<string, any>>,
    ) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },
    setWipStatus: (state, action: PayloadAction<number>) => {
      state.wipStatus = action.payload;
    },
    initializeForm: (
      state,
      action: PayloadAction<
        Partial<LegacyAutoFormData> & { wip_status?: number }
      >,
    ) => {
      const payload = action.payload;
      const clientInfo = payload.client_information;
      const binderInfo = payload.binder_information;
      const billing = payload.billing;
      const telematics = payload.telematics;
      const additionalInfo = payload.additional_info;
      const agencyBilling = billing?.agency_billing;

      state.wipStatus = payload.wip_status ?? null;
      state.premiumConfirmed = (payload as any).premium_confirmed || false;
      state.data = {
        client_information: {
          client_name: clientInfo?.client_name || '',
          client_code: clientInfo?.client_code || '',
          client_email: clientInfo?.client_email || '',
          is_remarket: clientInfo?.is_remarket || false,
          has_loa: clientInfo?.has_loa || false,
        },
        binder_information: {
          binder_number: binderInfo?.binder_number || '',
          effective_date: binderInfo?.effective_date || '',
          insurance_company: binderInfo?.insurance_company || '',
          premium:
            binderInfo?.premium != null ? String(binderInfo.premium) : '',
        },
        billing: {
          payment_date_requested: billing?.payment_date_requested || '',
          payment_type: billing?.payment_type || '',
          payment_method: billing?.payment_method || '',
          down_payment: billing?.down_payment || false,
          down_payment_amount: billing?.down_payment_amount || 0,
          down_payment_type: billing?.down_payment_type || '',
          agency_billing: {
            financing_type: agencyBilling?.financing_type || '',
            is_signed_contract_attached:
              agencyBilling?.is_signed_contract_attached || false,
            province_code: agencyBilling?.province_code || '',
            business_code: agencyBilling?.business_code || 'AUTO',
            total_premium: agencyBilling?.total_premium || '',
            company_fee: agencyBilling?.company_fee || '',
            broker_fee: agencyBilling?.broker_fee || '',
            tax: agencyBilling?.tax || '',
          },
        },
        telematics: {
          has_telematics: telematics?.has_telematics || false,
          driver_name: telematics?.driver_name || '',
          phone: telematics?.phone || '',
          email: telematics?.email || '',
        },
        additional_info: {
          all_documents_attached:
            additionalInfo?.all_documents_attached || false,
          underwriting_approval_required:
            additionalInfo?.underwriting_approval_required || false,
          underwriting_reason: additionalInfo?.underwriting_reason || '',
          paper_copies_required: additionalInfo?.paper_copies_required || false,
          paper_copy_reason: additionalInfo?.paper_copy_reason || '',
          additional_broker_note: additionalInfo?.additional_broker_note || '',
        },
      };
      state.isDataSet = true;
      state.hasUnsavedChanges = false;
    },
    resetForm: () => initialState,
  },
});

export const {
  setClientName,
  setClientCode,
  setClientEmail,
  setIsRemarket,
  setHasLoa,
  setBinderNumber,
  setEffectiveDate,
  setInsuranceCompany,
  setAllDocumentsAttached,
  setHasTelematics,
  setTelematicsDriverName,
  setTelematicsPhone,
  setTelematicsEmail,
  setPremium,
  setPaymentDateRequested,
  setPaymentType,
  setPaymentMethod,
  setDownPayment,
  setDownPaymentAmount,
  setDownPaymentType,
  setAgencyBillingFinancingType,
  setAgencyBillingSignedContractAttached,
  setAgencyBillingProvinceCode,
  setAgencyBillingBusinessCode,
  setAgencyBillingTotalPremium,
  setPremiumConfirmed,
  setAgencyBillingCompanyFee,
  setAgencyBillingBrokerFee,
  setAgencyBillingTax,
  setUnderwritingApprovalRequired,
  setUnderwritingReason,
  setPaperCopiesRequired,
  setPaperCopyReason,
  setAdditionalBrokerNote,
  setDataSet,
  setHasUnsavedChanges,
  setValidationErrors,
  clearValidationErrors,
  setWipStatus,
  initializeForm,
  resetForm,
} = legacyAutoFormSlice.actions;

export default legacyAutoFormSlice.reducer;
